import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, ArrowLeftRight, Plus, Send, Copy, X, Upload, CheckCircle, AlertCircle, Clock,
} from "lucide-react";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem("client_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtHTG = (n) => Number(n || 0).toLocaleString("fr-FR") + " G";

const STATUS_LABEL = {
  open: { label: "Disponible", color: "#16A34A", bg: "#DCFCE7" },
  claimed: { label: "Acceptée", color: "#1D4ED8", bg: "#DBEAFE" },
  creator_paid: { label: "Demandeur payé", color: "#A16207", bg: "#FEF9C3" },
  claimer_paid: { label: "Fournisseur payé", color: "#A16207", bg: "#FEF9C3" },
  both_paid: { label: "En attente admin", color: "#7C3AED", bg: "#EDE9FE" },
  settled: { label: "Terminée", color: "#15803D", bg: "#DCFCE7" },
  cancelled: { label: "Annulée", color: "#B91C1C", bg: "#FEE2E2" },
};

function commissionFor(amount) {
  const a = Number(amount || 0);
  if (a < 5000) return { req: 1.0, prov: 0.5 };
  if (a < 20000) return { req: 0.8, prov: 0.4 };
  return { req: 0.5, prov: 0.3 };
}

const ADMIN_ACCOUNTS = {
  moncash: { label: "MonCash Admin", number: "+50946261180" },
  natcash: { label: "NatCash Admin", number: "+50955460588" },
};

export default function HtgP2PExchange({ clientId }) {
  const [tab, setTab] = useState("offers"); // offers | mine
  const [offers, setOffers] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [proofModal, setProofModal] = useState(null); // {offerId, role}

  // Create form
  const [sendMethod, setSendMethod] = useState("moncash");
  const [sendAmount, setSendAmount] = useState("");
  const [recvMethod, setRecvMethod] = useState("natcash");
  const [recvAmount, setRecvAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tierAmount = Math.max(Number(sendAmount || 0), Number(recvAmount || 0));
  const tier = commissionFor(tierAmount);
  const previewCreatorFee = Math.round(Number(sendAmount || 0) * tier.req) / 100;
  const previewClaimerFee = Math.round(Number(recvAmount || 0) * tier.prov) / 100;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/p2p/htg/offers`, { headers: authHeaders() }).catch(() => ({ data: { offers: [] } })),
        axios.get(`${API}/p2p/htg/my-offers`, { headers: authHeaders() }).catch(() => ({ data: { offers: [] } })),
      ]);
      setOffers(a.data?.offers || []);
      setMyOffers(b.data?.offers || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    const t = setInterval(fetchAll, 20000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const submitOffer = async () => {
    if (sendMethod === recvMethod) { toast.error("Les méthodes doivent être différentes"); return; }
    const s = parseFloat(sendAmount), r = parseFloat(recvAmount);
    if (!s || !r || s < 100 || r < 100) { toast.error("Montants minimum : 100 G"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/p2p/htg/offers`, {
        send_method: sendMethod, send_amount: s,
        recv_method: recvMethod, recv_amount: r,
        note: note || undefined,
      }, { headers: authHeaders() });
      toast.success("Annonce publiée");
      setShowCreate(false);
      setSendAmount(""); setRecvAmount(""); setNote("");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const claim = async (id) => {
    if (!window.confirm("Accepter cette annonce d'échange ?")) return;
    try {
      await axios.post(`${API}/p2p/htg/offers/${id}/claim`, {}, { headers: authHeaders() });
      toast.success("Annonce acceptée ! Suivez les instructions de paiement.");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const cancelOffer = async (id) => {
    if (!window.confirm("Annuler votre annonce ?")) return;
    try {
      await axios.post(`${API}/p2p/htg/offers/${id}/cancel`, {}, { headers: authHeaders() });
      toast.success("Annulée");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const submitProof = async (offer, role, dataUrl) => {
    if (!dataUrl) { toast.error("Joignez une image"); return; }
    try {
      await axios.post(`${API}/p2p/htg/offers/${offer.id}/proof`, { proof_url: dataUrl }, { headers: authHeaders() });
      toast.success("Preuve enregistrée");
      setProofModal(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const copyToClipboard = (text) => {
    try { navigator.clipboard.writeText(text); toast.success("Copié"); } catch { /* ignore */ }
  };

  const renderOfferCard = (o, isMine) => {
    const status = STATUS_LABEL[o.status] || { label: o.status, color: "#475569", bg: "#F1F5F9" };
    const role = o.creator_id === clientId ? "creator" : (o.claimer_id === clientId ? "claimer" : null);
    const isCreator = role === "creator";
    const myMethod = isCreator ? o.creator_send_method : o.creator_recv_method;
    const myAmount = isCreator ? o.creator_send_amount : o.creator_recv_amount;
    const myNet = isCreator ? o.creator_net_receive : o.claimer_net_receive;
    const myFee = isCreator ? o.creator_fee_htg : o.claimer_fee_htg;
    const myProof = isCreator ? o.creator_proof_url : o.claimer_proof_url;
    const adminAcct = myMethod ? ADMIN_ACCOUNTS[myMethod] : null;

    return (
      <div key={o.id} data-testid={`htg-offer-${o.id}`} style={{
        background: "white", border: "1px solid #E2E8F0", borderRadius: 10, padding: "0.85rem",
        marginBottom: "0.6rem", boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.4rem" }}>
          <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>
            {fmtHTG(o.creator_send_amount)} <span style={{ color: "#94A3B8" }}>{o.creator_send_method.toUpperCase()}</span>
            {" ↔ "}
            {fmtHTG(o.creator_recv_amount)} <span style={{ color: "#94A3B8" }}>{o.creator_recv_method.toUpperCase()}</span>
          </div>
          <span style={{ background: status.bg, color: status.color, padding: "2px 8px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 700 }}>
            {status.label}
          </span>
        </div>
        <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: "0.3rem" }}>
          Par <strong>{o.creator_name || "—"}</strong> · {new Date(o.created_at).toLocaleString("fr-FR")}
        </div>
        {o.note && <div style={{ fontSize: "0.72rem", color: "#475569", marginTop: "0.2rem", fontStyle: "italic" }}>« {o.note} »</div>}

        {/* Commission summary visible on every card */}
        <div style={{ marginTop: "0.4rem", padding: "0.4rem 0.6rem", background: "#F8FAFC", borderRadius: 6, fontSize: "0.7rem", color: "#475569" }}>
          💼 Frais : Demandeur <b>{(o.creator_fee_pct * 100).toFixed(2)}%</b> ({fmtHTG(o.creator_fee_htg)}) · Fournisseur <b>{(o.claimer_fee_pct * 100).toFixed(2)}%</b> ({fmtHTG(o.claimer_fee_htg)})
        </div>

        {/* If this is MY offer and I have a role, show payment instructions */}
        {isMine && role && o.status !== "settled" && o.status !== "cancelled" && (
          <div style={{ marginTop: "0.5rem", padding: "0.5rem", background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 8, fontSize: "0.78rem" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>👉 Vous êtes le {isCreator ? "demandeur" : "fournisseur"}</div>
            <div>Envoyez <b>{fmtHTG(myAmount)}</b> via <b>{myMethod?.toUpperCase()}</b> au compte admin :</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <code style={{ background: "white", padding: "2px 6px", borderRadius: 4, fontWeight: 700 }} data-testid={`htg-admin-acc-${o.id}`}>{adminAcct?.number}</code>
              <button onClick={() => copyToClipboard(adminAcct?.number || "")} style={{ background: "#E0E7FF", color: "#3730A3", border: "none", padding: "2px 6px", borderRadius: 4, cursor: "pointer", fontSize: "0.7rem" }}>
                <Copy size={12} /> Copier
              </button>
            </div>
            <div style={{ marginTop: 4, color: "#15803D" }}>Vous recevrez ensuite <b>{fmtHTG(myNet)}</b> ({myFee.toFixed(2)} G de frais retenus).</div>
            {!myProof && (
              <button
                onClick={() => setProofModal({ offer: o, role })}
                style={{ marginTop: 8, background: "#2563EB", color: "white", border: "none", padding: "0.45rem 0.8rem", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: "0.78rem" }}
                data-testid={`htg-upload-proof-${o.id}`}
              >
                <Upload size={13} /> Téléverser ma preuve
              </button>
            )}
            {myProof && (
              <div style={{ marginTop: 6, color: "#15803D", fontWeight: 600 }}>
                <CheckCircle size={13} /> Preuve déjà envoyée — l'admin vérifie.
              </div>
            )}
          </div>
        )}

        {/* Cancel offered by creator while still open */}
        {isMine && isCreator && o.status === "open" && (
          <button onClick={() => cancelOffer(o.id)} style={{ marginTop: 6, background: "#FEE2E2", color: "#B91C1C", border: "none", padding: "0.3rem 0.7rem", borderRadius: 6, cursor: "pointer", fontSize: "0.75rem" }} data-testid={`htg-cancel-${o.id}`}>
            <X size={12} /> Annuler
          </button>
        )}

        {/* Claim from public list */}
        {!isMine && o.status === "open" && (
          <button onClick={() => claim(o.id)} style={{ marginTop: 6, background: "#16A34A", color: "white", border: "none", padding: "0.4rem 0.8rem", borderRadius: 6, cursor: "pointer", fontSize: "0.8rem", fontWeight: 600 }} data-testid={`htg-claim-${o.id}`}>
            <ArrowLeftRight size={13} /> Accepter cet échange
          </button>
        )}
      </div>
    );
  };

  const visibleOffers = tab === "offers" ? offers : myOffers;

  return (
    <div data-testid="htg-p2p-exchange">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <h3 style={{ margin: 0, fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 6 }}>
          <ArrowLeftRight size={18} color="#7C3AED" /> Échange Gourdes (MonCash ↔ NatCash)
        </h3>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={fetchAll} style={{ background: "#E2E8F0", color: "#0F172A", border: "none", padding: "0.4rem 0.7rem", borderRadius: 6, cursor: "pointer", fontSize: "0.75rem" }}>
            <RefreshCw size={12} /> Rafraîchir
          </button>
          <button onClick={() => setShowCreate(true)} style={{ background: "#7C3AED", color: "white", border: "none", padding: "0.4rem 0.8rem", borderRadius: 6, cursor: "pointer", fontSize: "0.78rem", fontWeight: 700 }} data-testid="htg-create-offer-btn">
            <Plus size={12} /> Créer une annonce
          </button>
        </div>
      </div>

      {/* Commission tier reference */}
      <div style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "0.55rem 0.7rem", marginBottom: "0.6rem", fontSize: "0.72rem", color: "#5B21B6" }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>📌 Frais selon le montant :</div>
        <div>• 100 – 4 999 G : Demandeur 1% · Fournisseur 0.5%</div>
        <div>• 5 000 – 19 999 G : Demandeur 0.8% · Fournisseur 0.4%</div>
        <div>• 20 000 G et + : Demandeur 0.5% · Fournisseur 0.3%</div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: "0.6rem" }}>
        <button onClick={() => setTab("offers")} style={{ flex: 1, background: tab === "offers" ? "#7C3AED" : "#E2E8F0", color: tab === "offers" ? "white" : "#0F172A", border: "none", padding: "0.4rem", borderRadius: 6, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }} data-testid="htg-tab-offers">
          📋 Annonces ouvertes ({offers.length})
        </button>
        <button onClick={() => setTab("mine")} style={{ flex: 1, background: tab === "mine" ? "#7C3AED" : "#E2E8F0", color: tab === "mine" ? "white" : "#0F172A", border: "none", padding: "0.4rem", borderRadius: 6, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }} data-testid="htg-tab-mine">
          🧾 Mes annonces ({myOffers.length})
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "1.5rem", color: "#64748B", fontSize: "0.85rem" }}>Chargement…</div>
      ) : visibleOffers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "1.5rem", color: "#64748B", fontSize: "0.85rem", background: "#F8FAFC", borderRadius: 8 }}>
          {tab === "offers" ? "Aucune annonce disponible pour le moment." : "Vous n'avez pas encore d'annonces. Créez-en une !"}
        </div>
      ) : (
        visibleOffers.map(o => renderOfferCard(o, tab === "mine"))
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateOfferModal
          onClose={() => setShowCreate(false)}
          sendMethod={sendMethod} setSendMethod={setSendMethod}
          sendAmount={sendAmount} setSendAmount={setSendAmount}
          recvMethod={recvMethod} setRecvMethod={setRecvMethod}
          recvAmount={recvAmount} setRecvAmount={setRecvAmount}
          note={note} setNote={setNote}
          tier={tier} tierAmount={tierAmount}
          previewCreatorFee={previewCreatorFee} previewClaimerFee={previewClaimerFee}
          submitting={submitting} onSubmit={submitOffer}
        />
      )}

      {/* Proof modal */}
      {proofModal && (
        <ProofUploadModal
          onClose={() => setProofModal(null)}
          offer={proofModal.offer}
          role={proofModal.role}
          onSubmit={(url) => submitProof(proofModal.offer, proofModal.role, url)}
        />
      )}
    </div>
  );
}

function CreateOfferModal({ onClose, sendMethod, setSendMethod, sendAmount, setSendAmount, recvMethod, setRecvMethod, recvAmount, setRecvAmount, note, setNote, tier, tierAmount, previewCreatorFee, previewClaimerFee, submitting, onSubmit }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 12, padding: "1rem", maxWidth: 460, width: "100%" }} data-testid="htg-create-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
          <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Créer une annonce d'échange</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem" }}><X size={18} /></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <div>
            <label style={{ fontSize: "0.72rem", color: "#475569", fontWeight: 600 }}>Je donne (méthode)</label>
            <select value={sendMethod} onChange={e => setSendMethod(e.target.value)} className="form-input" data-testid="htg-send-method" style={{ width: "100%", padding: "0.4rem", borderRadius: 6, border: "1px solid #CBD5E1" }}>
              <option value="moncash">MonCash</option>
              <option value="natcash">NatCash</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.72rem", color: "#475569", fontWeight: 600 }}>Je reçois (méthode)</label>
            <select value={recvMethod} onChange={e => setRecvMethod(e.target.value)} className="form-input" data-testid="htg-recv-method" style={{ width: "100%", padding: "0.4rem", borderRadius: 6, border: "1px solid #CBD5E1" }}>
              <option value="natcash">NatCash</option>
              <option value="moncash">MonCash</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: "0.72rem", color: "#475569", fontWeight: 600 }}>Montant donné (G)</label>
            <input type="number" value={sendAmount} onChange={e => setSendAmount(e.target.value)} min="100" data-testid="htg-send-amount" style={{ width: "100%", padding: "0.4rem", borderRadius: 6, border: "1px solid #CBD5E1" }} placeholder="Ex: 50100" />
          </div>
          <div>
            <label style={{ fontSize: "0.72rem", color: "#475569", fontWeight: 600 }}>Montant reçu (G)</label>
            <input type="number" value={recvAmount} onChange={e => setRecvAmount(e.target.value)} min="100" data-testid="htg-recv-amount" style={{ width: "100%", padding: "0.4rem", borderRadius: 6, border: "1px solid #CBD5E1" }} placeholder="Ex: 50000" />
          </div>
        </div>

        <label style={{ fontSize: "0.72rem", color: "#475569", fontWeight: 600 }}>Note (optionnel)</label>
        <input type="text" value={note} onChange={e => setNote(e.target.value)} maxLength={200} className="form-input" style={{ width: "100%", padding: "0.4rem", borderRadius: 6, border: "1px solid #CBD5E1", marginBottom: "0.5rem" }} placeholder="Ex: échange rapide souhaité" />

        {/* Live commission preview */}
        {tierAmount > 0 && (
          <div data-testid="htg-fee-preview" style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "0.6rem", marginBottom: "0.6rem", fontSize: "0.8rem" }}>
            <div style={{ fontWeight: 700, marginBottom: 3 }}>💼 Frais estimés</div>
            <div>• Votre commission (demandeur) : <b>{tier.req}%</b> = <b>{fmtHTG(previewCreatorFee)}</b></div>
            <div>• Commission de l'accepteur (fournisseur) : <b>{tier.prov}%</b> = <b>{fmtHTG(previewClaimerFee)}</b></div>
            <div style={{ marginTop: 4, color: "#A16207", fontSize: "0.7rem" }}>Frais retenus par l'admin sur chaque envoi. Vous recevrez votre montant net.</div>
          </div>
        )}

        <button onClick={onSubmit} disabled={submitting} style={{ width: "100%", background: "#7C3AED", color: "white", padding: "0.55rem", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }} data-testid="htg-submit-offer">
          {submitting ? "Publication…" : "Publier mon annonce"}
        </button>
      </div>
    </div>
  );
}

function ProofUploadModal({ onClose, offer, role, onSubmit }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image > 5 Mo"); return; }
    const reader = new FileReader();
    reader.onload = () => { setPreview(reader.result); setFile(f); };
    reader.readAsDataURL(f);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 12, padding: "1rem", maxWidth: 420, width: "100%" }} data-testid="htg-proof-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
          <h3 style={{ margin: 0, fontSize: "1rem" }}>Téléverser ma preuve</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: "0.6rem" }}>
          Téléversez la capture de votre paiement de {fmtHTG(role === "creator" ? offer.creator_send_amount : offer.creator_recv_amount)} sur {role === "creator" ? offer.creator_send_method : offer.creator_recv_method}.
        </p>
        <input type="file" accept="image/*" onChange={handlePick} data-testid="htg-proof-file" />
        {preview && <img src={preview} alt="preview" style={{ width: "100%", marginTop: 8, borderRadius: 6, maxHeight: 200, objectFit: "contain" }} />}
        <button onClick={() => onSubmit(preview)} disabled={!file} style={{ width: "100%", marginTop: 8, background: "#2563EB", color: "white", padding: "0.5rem", border: "none", borderRadius: 8, fontWeight: 600, cursor: file ? "pointer" : "not-allowed", opacity: file ? 1 : 0.5 }} data-testid="htg-submit-proof">
          <Upload size={14} /> Envoyer la preuve
        </button>
      </div>
    </div>
  );
}
